import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, EID_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { cx } from '@/utils/cx'

interface NameResult {
  checkType?: string
  result?: 'failure' | 'success'
  actual?: string
  score?: number
  comparison?: string
  numberOfCharactersMismatched?: number
  expected?: string
}

interface DateOfBirthResult {
  checkType?: string
  result?: 'failure' | 'success'
  actual?: string
  comparison?: string
  expected?: string
}

interface GetContextResponse {
  isRetryAttempt: boolean
  expectedData: {
    firstName: string | undefined
    lastName: string | undefined
    dateOfBirth: string | undefined
  }
  actualData: {
    firstName: string | undefined
    lastName: string | undefined
    dateOfBirth: string | undefined
  }
  corroborationResult?: {
    result?: 'failure' | 'success'
    eid?: {
      result?: 'failure' | 'success'
      score?: boolean
      firstName?: NameResult[]
      lastName?: NameResult[]
      dateOfBirth?: DateOfBirthResult[]
    }
    rejectionReasons?: string[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customClientFields?: any
    status?: string
  }
}

interface GenerateProceedUrlResponse {
  proceedUrl: string
}

export const EidElavonPage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const {
    RetryElavonPage: { eid: trans },
  } = store.TranslationsState.translations
  const { screenSize, isMobile } = store.AppState

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [successFirstName, setSuccessFirstName] = useState<boolean>(false)
  const [successLastName, setSuccessLastName] = useState<boolean>(false)
  const [successDateOfBirth, setSuccessDateOfBirth] = useState<boolean>(false)
  const [expectedFirstName, setExpectedFirstName] = useState<string | undefined>()
  const [expectedLastName, setExpectedLastName] = useState<string | undefined>()
  const [expectedDateOfBirth, setExpectedDateOfBirth] = useState<string | undefined>()
  const [actualFirstName, setActualFirstName] = useState<string | undefined>()
  const [actualLastName, setActualLastName] = useState<string | undefined>()
  const [actualDateOfBirth, setActualDateOfBirth] = useState<string | undefined>()

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const generateProceedUrl = async () => {
    try {
      const { data } = await axiosInstance.post<GenerateProceedUrlResponse>(
        `${API_URL}/${EID_SERVICE_PATH}/generate-proceed-url`,
        {},
        {
          withCredentials: true,
        }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (error) {
      devLog('Error generating url:', error)
    }
  }

  const getContext = async () => {
    try {
      const { data } = await axiosInstance.get<GetContextResponse>(
        `${API_URL}/${EID_SERVICE_PATH}/get-context`,
        {
          withCredentials: true,
        }
      )

      if (!data?.isRetryAttempt) {
        generateProceedUrl()
      } else {
        const firstName = data?.corroborationResult?.eid?.firstName
        const lastName = data?.corroborationResult?.eid?.lastName
        const dateOfBirth = data?.corroborationResult?.eid?.dateOfBirth

        firstName?.length && setSuccessFirstName(firstName[0].result === 'success')
        firstName?.length && setActualFirstName(firstName[0].actual)
        firstName?.length && setExpectedFirstName(firstName[0].expected)

        lastName?.length && setSuccessLastName(lastName[0].result === 'success')
        lastName?.length && setActualLastName(lastName[0].actual)
        lastName?.length && setExpectedLastName(lastName[0].expected)

        dateOfBirth?.length && setSuccessDateOfBirth(dateOfBirth[0].result === 'success')
        dateOfBirth?.length && setActualDateOfBirth(dateOfBirth[0].actual)
        dateOfBirth?.length && setExpectedDateOfBirth(dateOfBirth[0].expected)

        setIsLoading(false)
      }
    } catch (error) {
      devLog('Error fetching context:', error)
    }
  }

  const handleProceed = () => {
    setBtnLoading(true)
    generateProceedUrl()
  }

  useEffect(() => {
    if (screenSize.width && screenSize.width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight}px - 120px)`)
    }
  }, [screenSize.width])

  useEffect(() => {
    getContext()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph">{trans.description}</p>

        <div className="generic__spacer h-4" />

        <div className="elavon__paragraph-with-icon">
          <p className="generic__paragraph">{trans.firstName}</p>
          {!successFirstName ? (
            <Icon type="mismatch" size="18px" color="#D83535" />
          ) : (
            <Icon type="check-circle" size="18px" color="#467723" />
          )}
        </div>

        <div className="elavon__expected-name-wrapper">{expectedFirstName}</div>
        <div className={cx('elavon__actual-name-wrapper', !successFirstName && 'error')}>
          {actualFirstName}
        </div>

        <div className="generic__spacer h-4" />

        <div className="elavon__paragraph-with-icon">
          <p className="generic__paragraph">{trans.lastName}</p>
          {!successLastName ? (
            <Icon type="mismatch" size="18px" color="#D83535" />
          ) : (
            <Icon type="check-circle" size="18px" color="#467723" />
          )}
        </div>

        <div className="elavon__expected-name-wrapper">{expectedLastName}</div>
        <div className={cx('elavon__actual-name-wrapper', !successLastName && 'error')}>
          {actualLastName}
        </div>

        <div className="generic__spacer h-4" />

        <div className="elavon__paragraph-with-icon">
          <p className="generic__paragraph">{trans.dateOfBirth}</p>
          {!successDateOfBirth ? (
            <Icon type="mismatch" size="18px" color="#D83535" />
          ) : (
            <Icon type="check-circle" size="18px" color="#467723" />
          )}
        </div>

        <div className="elavon__expected-name-wrapper">{expectedDateOfBirth}</div>
        <div className={cx('elavon__actual-name-wrapper', !successDateOfBirth && 'error')}>
          {actualDateOfBirth}
        </div>

        <div className="generic__spacer h-30" />

        <div className="elavon__warning">
          <div className="elavon__step-icon warning">
            <Icon type="warning" size="18px" color="#303438" />
          </div>
          <div className="elavon__info-text">{trans.tryDifferentAccount}</div>
        </div>

        {isMobile ? (
          <div className="layout__margined m-0-0-40" />
        ) : (
          <div className="layout__margined m-0-0-15" />
        )}
      </div>

      <div className="layout__content-container-bottom-fixed-centered">
        <Button onClick={handleProceed} tabIndex={0} className="button--proceed" ref={buttonRef}>
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
            data-testId="proceed"
          >
            {trans.tryAgain}
          </span>
        </Button>
      </div>
    </div>
  )
})
